// Phase 6 + 9 — Taxes page (year-agnostic) with 6 tabs
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileText, Loader2, Receipt, Briefcase, Building2, Banknote, Info, Calculator, ClipboardList, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (v) => `$${Number(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

function ActionDialog({ open, onClose, title, fields, onSave }) {
  const [vals, setVals] = useState({});
  useEffect(() => { if (!open) setVals({}); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(f => (
            <div key={f.name}>
              <Label>{f.label}</Label>
              <Input type={f.type||"text"} value={vals[f.name]||""}
                onChange={e=>setVals({...vals,[f.name]:e.target.value})}
                data-testid={`field-${f.name}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" onClick={()=>onSave(vals)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({s}){const m={paid:"bg-emerald-100 text-emerald-700",filed:"bg-sky-100 text-sky-700",unpaid:"bg-amber-100 text-amber-700",posted:"bg-emerald-100 text-emerald-700"};return <Badge className={`${m[s]||"bg-slate-100"} border-0`}>{s}</Badge>;}

function RemittedTaxPayments({ rows, taxType }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card data-testid={`remitted-tax-payments-${taxType}`}>
      <CardContent className="p-4 space-y-2">
        <div>
          <h3 className="font-semibold">Remitted Tax Payments</h3>
          <p className="text-xs text-slate-500">From posted tax_payment transactions</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Counterparty</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.transaction_id} data-testid={`remitted-row-${r.transaction_id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell>{r.counterparty || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.reference || "—"}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{`$${Number(r.amount||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`}</TableCell>
                <TableCell><StatusBadge s={r.status || "posted"}/></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function HelpBanner({ title, children, accent = "sky" }) {
  const palette = {
    sky:     "bg-sky-50 border-sky-200 text-sky-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    violet:  "bg-violet-50 border-violet-200 text-violet-900",
  }[accent];
  return (
    <div className={`rounded-md border ${palette} p-3 flex items-start gap-3`} data-testid="tax-help-banner">
      <Info className="h-4 w-4 mt-0.5 shrink-0 opacity-70" />
      <div className="space-y-1 text-sm">
        <p className="font-semibold">{title}</p>
        <div className="text-[13px] leading-relaxed opacity-90">{children}</div>
      </div>
    </div>
  );
}

function pct(v) { try { return `${(Number(v||0)*100).toFixed(2)}%`; } catch { return "—"; } }

export default function TaxesPage() {
  const { user } = useAuth();
  const canRecord = ["system_admin", "salihi_accountant", "salihi_senior_accountant", "salihi_finance_manager"].includes(user?.role)
                    || user?.full_access === true;
  const canDelete = ["system_admin", "salihi_finance_manager"].includes(user?.role)
                    || user?.full_access === true;
  const [tab, setTab] = useState("payroll");
  const [payroll, setPayroll] = useState([]);
  const [payrollRemitted, setPayrollRemitted] = useState([]);
  const [vat, setVat] = useState(null);
  const [wht, setWht] = useState(null);
  const [cit, setCit] = useState(null);
  const [taxSettings, setTaxSettings] = useState(null);
  const [calc, setCalc] = useState(null);
  const [records, setRecords] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v, w, c, ts, calcRes, recRes] = await Promise.all([
        api.get(`/taxes/payroll?year=${year}`),
        api.get(`/taxes/vat?year=${year}`),
        api.get(`/taxes/wht?year=${year}`),
        api.get(`/taxes/corporate?year=${year}`),
        api.get(`/tax-settings`),
        api.get(`/taxes/calculator?year=${year}`),
        api.get(`/taxes/records?year=${year}`),
      ]);
      setPayroll(p.data.items); setPayrollRemitted(p.data.remitted_payments || []);
      setVat(v.data); setWht(w.data); setCit(c.data); setTaxSettings(ts.data);
      setCalc(calcRes.data); setRecords(recRes.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(()=>{load();},[load]);

  async function doFiling(taxType, period, vals) {
    try {
      await api.post("/tax-filings", { tax_type:taxType, period_label:period, reference:vals.reference, filed_date:vals.filed_date, notes:vals.notes });
      toast.success("Filing recorded"); setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function doPayment(taxType, period, vals) {
    try {
      await api.post("/tax-payments", { tax_type:taxType, period_label:period, amount:parseFloat(vals.amount), payment_date:vals.payment_date, bank_account_code:vals.bank_account_code||"1010", reference:vals.reference });
      toast.success("Payment posted (balanced JE)"); setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function exportReport(reportId, format) {
    const token = localStorage.getItem("st_token");
    fetch(`${API}/reports/${reportId}/${format}?period_start=${year}-01-01&period_end=${year}-12-31`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { const b=await r.blob(); const a=document.createElement("a"); a.href=URL.createObjectURL(b); a.download=`${reportId}.${format}`; a.click(); });
  }
  function downloadWHTCert(txId, ref) {
    const token = localStorage.getItem("st_token");
    fetch(`${API}/taxes/wht/certificate/${txId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { const b=await r.blob(); const a=document.createElement("a"); a.href=URL.createObjectURL(b); a.download=`WHT-Cert-${ref}.pdf`; a.click(); });
  }
  function downloadTaxSummary() {
    const token = localStorage.getItem("st_token");
    fetch(`${API}/tax-settings/summary.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { const b=await r.blob(); const a=document.createElement("a"); a.href=URL.createObjectURL(b); a.download=`SOMTURK-Tax-Settings-Summary.pdf`; a.click(); });
  }

  if (loading || !vat) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#4FA3D1]" /></div>;

  return (
    <div className="space-y-5" data-testid="taxes-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Taxes</h2>
          <p className="text-sm text-slate-500 mt-1">Payroll (PAYE), Sales Tax (VAT), Withholding (WHT), and Corporate tax management — per Somali tax law.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadTaxSummary} data-testid="print-tax-summary-btn">
            <FileDown className="h-4 w-4 mr-1.5" />Print Tax Settings Summary
          </Button>
          <label className="text-xs uppercase tracking-wide text-slate-500 ml-2" htmlFor="tax-year-sel">Year</label>
          <select
            id="tax-year-sel"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="tax-year-select"
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm bg-white"
          >
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 4 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="taxes-tabs">
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Banknote className="h-4 w-4 mr-1.5"/>Payroll Tax</TabsTrigger>
          <TabsTrigger value="vat" data-testid="tab-vat"><Receipt className="h-4 w-4 mr-1.5"/>Sales Tax (VAT)</TabsTrigger>
          <TabsTrigger value="wht" data-testid="tab-wht"><Briefcase className="h-4 w-4 mr-1.5"/>Withholding</TabsTrigger>
          <TabsTrigger value="cit" data-testid="tab-cit"><Building2 className="h-4 w-4 mr-1.5"/>Corporate Tax</TabsTrigger>
          <TabsTrigger value="calculator" data-testid="tab-calculator"><Calculator className="h-4 w-4 mr-1.5"/>Tax Calculator</TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records"><ClipboardList className="h-4 w-4 mr-1.5"/>Tax Records</TabsTrigger>
        </TabsList>

        <TabsContent value="payroll" className="mt-4 space-y-3">
          {taxSettings && <HelpBanner title={`Payroll Tax (PAYE) — applied to year ${year}`} accent="sky">
            Withheld each pay run from employee wages, then remitted to the tax authority. Current monthly brackets:&nbsp;
            {(taxSettings.paye_brackets||[]).map((b,i)=>(
              <span key={i} className="font-medium">{i>0 && " · "}{b.min?`>${b.min}`:"≤"}–{b.max?b.max:"∞"} @ {pct(b.rate)}</span>
            ))}. Non-residents flat {pct(taxSettings.paye_non_resident_rate)}, under-18 {pct(taxSettings.paye_under_18_rate)}.
            Filing due day {taxSettings.paye_filing_day} of the following month.
          </HelpBanner>}
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead className="text-right">Wages</TableHead><TableHead className="text-right">PAYE</TableHead><TableHead>Status</TableHead><TableHead>Due</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{payroll.length===0?<TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No posted payroll runs yet.</TableCell></TableRow>:
                payroll.map(r=>(<TableRow key={r.period}><TableCell>{r.period}</TableCell><TableCell className="text-right tabular-nums">{fmt(r.wages)}</TableCell><TableCell className="text-right tabular-nums">{fmt(r.paye)}</TableCell><TableCell><StatusBadge s={r.status}/></TableCell><TableCell className={r.overdue?"text-red-700 font-semibold":""}>{r.due_date}</TableCell><TableCell className="text-right space-x-1">
                  {r.status==="unpaid" && <Button size="sm" variant="outline" onClick={()=>setDlg({type:"filing",taxType:"payroll",period:r.period})} data-testid={`file-payroll-${r.period}`}>File</Button>}
                  {r.status!=="paid" && <Button size="sm" className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" onClick={()=>setDlg({type:"payment",taxType:"payroll",period:r.period,amount:r.paye})} data-testid={`pay-payroll-${r.period}`}>Pay</Button>}
                </TableCell></TableRow>))}
              </TableBody></Table>
          </CardContent></Card>
          <RemittedTaxPayments rows={payrollRemitted} taxType="payroll" />
        </TabsContent>

        <TabsContent value="vat" className="mt-4 space-y-3">
          {taxSettings && <HelpBanner title={`Sales Tax (VAT) — applied to year ${year}`} accent="emerald">
            A flat tax on the net value of goods/services. <span className="font-medium">Current standard rate: {pct(taxSettings.sales_tax_rate)}</span>. Output VAT (charged on sales) is remitted monthly to the tax authority.
          </HelpBanner>}
          <div className="grid sm:grid-cols-3 gap-3"><Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Total net sales</p><p className="text-2xl font-bold tabular-nums">{fmt(vat.total_net)}</p></CardContent></Card><Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Output VAT</p><p className="text-2xl font-bold tabular-nums text-emerald-700">{fmt(vat.total_vat)}</p></CardContent></Card><Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">VAT-bearing invoices</p><p className="text-2xl font-bold tabular-nums">{vat.items.length}</p></CardContent></Card></div>
          <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">VAT</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader><TableBody>
            {vat.by_month.length===0?<TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">No VAT activity.</TableCell></TableRow>:
              vat.by_month.map(r=>(<TableRow key={r.period}><TableCell>{r.period}</TableCell><TableCell className="text-right tabular-nums">{fmt(r.net)}</TableCell><TableCell className="text-right tabular-nums font-semibold">{fmt(r.vat)}</TableCell><TableCell><StatusBadge s={r.status}/></TableCell><TableCell className="text-right space-x-1">
                {r.status==="unpaid" && <Button size="sm" variant="outline" onClick={()=>setDlg({type:"filing",taxType:"vat",period:r.period})}>File</Button>}
                {r.status!=="paid" && <Button size="sm" className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" onClick={()=>setDlg({type:"payment",taxType:"vat",period:r.period,amount:r.vat})}>Pay</Button>}
              </TableCell></TableRow>))}
          </TableBody></Table></CardContent></Card>
          <RemittedTaxPayments rows={vat.remitted_payments} taxType="vat" />
          <div className="flex gap-2"><Button variant="outline" onClick={()=>exportReport("vat_summary","pdf")}><FileDown className="h-4 w-4 mr-1.5"/>Export PDF</Button><Button variant="outline" onClick={()=>exportReport("vat_summary","xlsx")}><FileText className="h-4 w-4 mr-1.5"/>Export Excel</Button></div>
        </TabsContent>

        <TabsContent value="wht" className="mt-4 space-y-3">
          {taxSettings && <HelpBanner title={`Withholding Tax (WHT) — applied to year ${year}`} accent="amber">
            Tax withheld at source on specific payments to suppliers (rentals, royalties, services, dividends, etc.). The supplier is paid net of WHT and receives a certificate that credits their final tax. Current rates per category are configured in Tax Settings.
          </HelpBanner>}
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Total withheld in period</p><p className="text-3xl font-bold tabular-nums">{fmt(wht.total)}</p></CardContent></Card>
          <div className="grid lg:grid-cols-2 gap-3">
            <Card><CardContent className="p-4"><h3 className="font-semibold mb-2">By Category</h3><Table><TableBody>{wht.by_category.map(c=>(<TableRow key={c.category}><TableCell>{c.category}</TableCell><TableCell className="text-right tabular-nums font-semibold">{fmt(c.total)}</TableCell><TableCell className="text-right text-slate-500">{c.count} tx</TableCell></TableRow>))}</TableBody></Table></CardContent></Card>
            <Card><CardContent className="p-4"><h3 className="font-semibold mb-2">By Month</h3><Table><TableBody>{wht.by_month.map(m=>(<TableRow key={m.period}><TableCell>{m.period}</TableCell><TableCell className="text-right tabular-nums">{fmt(m.total)}</TableCell><TableCell><StatusBadge s={m.status}/></TableCell><TableCell className="text-right">{m.status!=="paid"&&<Button size="sm" className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" onClick={()=>setDlg({type:"payment",taxType:"wht",period:m.period,amount:m.total})}>Pay</Button>}</TableCell></TableRow>))}</TableBody></Table></CardContent></Card>
          </div>
          <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Supplier</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">WHT</TableHead><TableHead className="text-right">Cert</TableHead></TableRow></TableHeader><TableBody>{wht.items.map(t=>(<TableRow key={t.id}><TableCell>{t.transaction_date}</TableCell><TableCell>{t.counterparty_name}</TableCell><TableCell><Badge variant="secondary">{t.wht_category}</Badge></TableCell><TableCell className="text-right tabular-nums">{fmt(t.amount)}</TableCell><TableCell className="text-right tabular-nums font-semibold">{fmt(t.wht_amount)}</TableCell><TableCell className="text-right"><Button size="sm" variant="outline" onClick={()=>downloadWHTCert(t.id,t.reference_number)} data-testid={`cert-${t.id}`}><FileDown className="h-3 w-3 mr-1"/>Cert</Button></TableCell></TableRow>))}</TableBody></Table></CardContent></Card>
          <RemittedTaxPayments rows={wht.remitted_payments} taxType="wht" />
          <div className="flex gap-2"><Button variant="outline" onClick={()=>exportReport("wht_summary","pdf")}><FileDown className="h-4 w-4 mr-1.5"/>Export PDF</Button><Button variant="outline" onClick={()=>exportReport("wht_summary","xlsx")}><FileText className="h-4 w-4 mr-1.5"/>Export Excel</Button></div>
        </TabsContent>

        <TabsContent value="cit" className="mt-4 space-y-3">
          {taxSettings && <HelpBanner title={`Corporate Income Tax — applied to year ${year}`} accent="violet">
            Tiered by annual sales: a fixed fee for small businesses, a % of sales for medium, or a % of net profit for larger businesses. Quarterly due dates: {(taxSettings.corporate_quarterly_dates||[]).join(", ")} · Final return: {taxSettings.corporate_annual_filing_date} · Non-resident rate: {pct(taxSettings.corporate_non_resident_rate)}.
            {cit?.tier_applied && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-violet-100 px-2.5 py-1 text-violet-900 font-semibold" data-testid="tier-indicator">
                Your business is currently in: {cit.tier_applied.type === "fixed" ? `Fixed-fee tier (${fmt(cit.tier_applied.value)})` : cit.tier_applied.type === "pct_sales" ? `% of sales tier (${pct(cit.tier_applied.value)} × sales)` : `% of profit tier (${pct(cit.tier_applied.value)} × net profit)`}
              </div>
            )}
          </HelpBanner>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Annual sales</p><p className="text-xl font-bold tabular-nums">{fmt(cit.sales)}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Net profit</p><p className="text-xl font-bold tabular-nums">{fmt(cit.net_profit)}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">CIT liability</p><p className="text-xl font-bold tabular-nums text-rose-700">{fmt(cit.liability)}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Paid YTD</p><p className="text-xl font-bold tabular-nums text-emerald-700">{fmt(cit.paid_ytd)}</p></CardContent></Card>
          </div>
          <Card><CardContent className="p-4"><h3 className="font-semibold mb-2">Tier applied</h3><p className="text-sm">{cit.tier_applied?`${cit.tier_applied.type} @ ${cit.tier_applied.value}× (sales ${fmt(cit.tier_applied.sales_min)}–${cit.tier_applied.sales_max?fmt(cit.tier_applied.sales_max):"∞"})`:"—"}</p></CardContent></Card>
          <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Quarter</TableHead><TableHead>Due</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader><TableBody>{cit.quarterly.map(q=>(<TableRow key={q.q}><TableCell>Q{q.q}</TableCell><TableCell>{q.due_date}</TableCell><TableCell className="text-right tabular-nums">{fmt(q.amount)}</TableCell><TableCell className="text-right"><Button size="sm" className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" onClick={()=>setDlg({type:"payment",taxType:"cit",period:String(year),amount:q.amount,quarter:q.q})}>Pay installment</Button></TableCell></TableRow>))}</TableBody></Table></CardContent></Card>
          <RemittedTaxPayments rows={cit.remitted_payments} taxType="cit" />
          <div className="flex gap-2"><Button variant="outline" onClick={()=>exportReport("corporate_tax","pdf")}><FileDown className="h-4 w-4 mr-1.5"/>Export PDF</Button><Button variant="outline" onClick={()=>exportReport("corporate_tax","xlsx")}><FileText className="h-4 w-4 mr-1.5"/>Export Excel</Button></div>
        </TabsContent>

        {/* ------------------- Tab 5: Tax Calculator (Phase 9) ------------------- */}
        <TabsContent value="calculator" className="mt-4 space-y-3" data-testid="tab-content-calculator">
          <HelpBanner title={`Tax Calculator — estimate for year ${year}`} accent="sky">
            Automatic estimate of taxes owed based on all submitted and approved transactions during the selected period. Use this to forecast tax payments before they're due. Estimates can differ from actual amounts paid — see the <strong>Tax Records</strong> tab for what was actually remitted.
          </HelpBanner>
          {!calc ? <Card><CardContent className="p-12 text-center text-slate-400">No calculator data.</CardContent></Card> : (
            <div className="grid lg:grid-cols-2 gap-3">
              <CalcCard
                icon={<Banknote className="h-5 w-5 text-sky-700"/>}
                title="Payroll Tax (PAYE)"
                amount={calc.paye.estimate} subtitle={`Based on ${calc.paye.source_tx} posted JE line${calc.paye.source_tx===1?"":"s"}`}
                explanation={calc.paye.explanation}
                accent="sky"
              />
              <CalcCard
                icon={<Receipt className="h-5 w-5 text-emerald-700"/>}
                title="Sales Tax (VAT)"
                amount={calc.vat.estimate} subtitle={`Based on ${calc.vat.source_tx} posted JE line${calc.vat.source_tx===1?"":"s"}`}
                explanation={calc.vat.explanation}
                accent="emerald"
              />
              <CalcCard
                icon={<Briefcase className="h-5 w-5 text-amber-700"/>}
                title="Withholding Tax (WHT)"
                amount={calc.wht.estimate} subtitle={`Based on ${calc.wht.source_tx} transactions`}
                explanation={calc.wht.explanation}
                accent="amber"
                rows={calc.wht.by_category}
              />
              <CalcCard
                icon={<Building2 className="h-5 w-5 text-violet-700"/>}
                title="Corporate Income Tax"
                amount={calc.corporate.estimate}
                subtitle={`Sales ${fmt(calc.corporate.sales)} · Net profit ${fmt(calc.corporate.net_profit)}`}
                explanation={calc.corporate.explanation}
                accent="violet"
                tier={calc.corporate.tier_applied}
              />
            </div>
          )}
          {calc?.as_of && <p className="text-xs text-slate-400">Estimate as of {calc.as_of.slice(0,19).replace("T"," ")} UTC.</p>}
        </TabsContent>

        {/* ------------------- Tab 6: Tax Records (Phase 9) ------------------- */}
        <TabsContent value="records" className="mt-4 space-y-3" data-testid="tab-content-records">
          <HelpBanner title={`Tax Records — actual payments in ${year}`} accent="emerald">
            Record actual tax payments made to the authority. Actual amounts may differ from the calculator's estimate due to adjustments, audits, or carry-overs. This is the source of truth for what was remitted.
          </HelpBanner>
          <div className="flex justify-end">
            {canRecord && (
              <Button onClick={()=>setShowRecordModal(true)} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="add-tax-record-btn">
                <Plus className="h-4 w-4 mr-1.5"/>Record Tax Payment
              </Button>
            )}
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Paid Date</TableHead><TableHead>Tax Type</TableHead><TableHead>Period</TableHead>
                <TableHead className="text-right">Estimated</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead>Filing Ref.</TableHead><TableHead>Status</TableHead>
                {canDelete && <TableHead className="text-right">Actions</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow><TableCell colSpan={canDelete?9:8} className="text-center py-8 text-slate-400">No tax payments recorded for {year} yet.</TableCell></TableRow>
                ) : records.map(r => {
                  const v = Number(r.variance || 0);
                  const cls = v > 0 ? "text-rose-700" : v < 0 ? "text-emerald-700" : "text-slate-500";
                  return (
                    <TableRow key={r.id} data-testid={`tax-record-${r.id}`}>
                      <TableCell>{r.paid_date}</TableCell>
                      <TableCell><Badge variant="secondary">{String(r.tax_type).toUpperCase()}</Badge></TableCell>
                      <TableCell>{r.period_label}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.estimated_amount)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmt(r.actual_amount)}</TableCell>
                      <TableCell className={`text-right tabular-nums font-semibold ${cls}`}>
                        {v > 0 ? "+" : ""}{fmt(v)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.filing_reference || "—"}</TableCell>
                      <TableCell><StatusBadge s={r.status}/></TableCell>
                      {canDelete && (
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={async ()=>{
                            if (!window.confirm(`Delete this tax record? This will also remove the linked journal entry.`)) return;
                            try { await api.delete(`/taxes/records/${r.id}`); toast.success("Tax record deleted"); load(); }
                            catch (e) { toast.error(formatApiError(e)); }
                          }} data-testid={`del-record-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600"/></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {dlg?.type==="filing" && <ActionDialog open onClose={()=>setDlg(null)} title={`Record ${dlg.taxType.toUpperCase()} filing — ${dlg.period}`}
        fields={[{name:"reference",label:"Filing reference"},{name:"filed_date",label:"Filed date",type:"date"},{name:"notes",label:"Notes"}]}
        onSave={(v)=>doFiling(dlg.taxType,dlg.period,v)}/>}
      {dlg?.type==="payment" && <ActionDialog open onClose={()=>setDlg(null)} title={`Record ${dlg.taxType.toUpperCase()} payment — ${dlg.period}`}
        fields={[{name:"amount",label:`Amount (suggested: ${fmt(dlg.amount||0)})`,type:"number"},{name:"payment_date",label:"Payment date",type:"date"},{name:"bank_account_code",label:"Bank account code (default 1010)"},{name:"reference",label:"Reference"}]}
        onSave={(v)=>doPayment(dlg.taxType,dlg.period,v)}/>}

      {showRecordModal && (
        <RecordTaxPaymentDialog open={showRecordModal} onClose={()=>setShowRecordModal(false)}
          calc={calc} year={year}
          onSaved={()=>{ setShowRecordModal(false); load(); }} />
      )}
    </div>
  );
}

/* ------------------- Phase 9 helper components ------------------- */

function CalcCard({ icon, title, amount, subtitle, explanation, accent = "sky", rows, tier }) {
  const ring = {
    sky:     "border-l-sky-500 bg-sky-50/30",
    emerald: "border-l-emerald-500 bg-emerald-50/30",
    amber:   "border-l-amber-500 bg-amber-50/30",
    violet:  "border-l-violet-500 bg-violet-50/30",
  }[accent];
  return (
    <Card className={`border-l-4 ${ring}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">{icon}<h3 className="font-semibold text-slate-900">{title}</h3></div>
          <p className="text-3xl font-bold tabular-nums text-slate-900">{fmt(amount)}</p>
        </div>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        <p className="text-[13px] text-slate-600 leading-relaxed">{explanation}</p>
        {tier && (
          <div className="rounded-md border border-violet-200 bg-violet-50 p-2 text-[12px] text-violet-900">
            Tier applied: <strong className="capitalize">{String(tier.type).replace("_", " ")}</strong> @ {tier.value}{tier.type !== "fixed" ? "×" : ""} (sales range {fmt(tier.sales_min)}–{tier.sales_max ? fmt(tier.sales_max) : "∞"})
          </div>
        )}
        {rows && rows.length > 0 && (
          <div className="rounded-md border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead className="text-xs">Category</TableHead><TableHead className="text-xs text-right">Count</TableHead><TableHead className="text-xs text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>{rows.map(r => (
                <TableRow key={r.category}><TableCell className="capitalize text-xs">{String(r.category).replace(/_/g," ")}</TableCell><TableCell className="text-right text-xs">{r.count}</TableCell><TableCell className="text-right text-xs font-semibold tabular-nums">{fmt(r.total)}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecordTaxPaymentDialog({ open, onClose, calc, year, onSaved }) {
  const [taxType, setTaxType] = useState("vat");
  const [periodLabel, setPeriodLabel] = useState(`${year}-${String(new Date().getMonth()+1).padStart(2,"0")}`);
  const [estimated, setEstimated] = useState("");
  const [actual, setActual] = useState("");
  const [filingRef, setFilingRef] = useState("");
  const [filedDate, setFiledDate] = useState("");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0,10));
  const [bank, setBank] = useState("1010");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Auto-prefill estimate from calculator when tax type changes
  useEffect(() => {
    if (!calc) return;
    const est = ({ payroll: calc.paye, vat: calc.vat, wht: calc.wht, cit: calc.corporate }[taxType] || {}).estimate;
    if (est !== undefined && est !== null) setEstimated(String(est));
  }, [taxType, calc]);

  const submit = async () => {
    if (!actual || Number(actual) <= 0) { toast.error("Actual amount must be > 0"); return; }
    if (!paidDate) { toast.error("Paid date is required"); return; }
    setSaving(true);
    try {
      await api.post("/taxes/records", {
        tax_type: taxType,
        period_label: periodLabel,
        estimated_amount: Number(estimated) || 0,
        actual_amount: Number(actual),
        filing_reference: filingRef || null,
        filed_date: filedDate || null,
        paid_date: paidDate,
        bank_account_code: bank || "1010",
        notes: notes || null,
      });
      toast.success("Tax record saved — balanced JE posted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const variance = (Number(actual) || 0) - (Number(estimated) || 0);
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="record-tax-payment-dialog">
        <DialogHeader><DialogTitle>Record Tax Payment</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
          <div>
            <Label className="text-xs">Tax type</Label>
            <Select value={taxType} onValueChange={setTaxType}>
              <SelectTrigger data-testid="rec-tax-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="payroll">Payroll (PAYE)</SelectItem>
                <SelectItem value="vat">Sales Tax (VAT)</SelectItem>
                <SelectItem value="wht">Withholding (WHT)</SelectItem>
                <SelectItem value="cit">Corporate Tax (CIT)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Period (e.g. 2026-06 or Q1 2026 or 2026)</Label>
            <Input value={periodLabel} onChange={e=>setPeriodLabel(e.target.value)} data-testid="rec-period"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Estimated</Label>
              <Input type="number" step="0.01" value={estimated} onChange={e=>setEstimated(e.target.value)} data-testid="rec-estimated"/>
            </div>
            <div>
              <Label className="text-xs">Actual paid <span className="text-rose-600">*</span></Label>
              <Input type="number" step="0.01" value={actual} onChange={e=>setActual(e.target.value)} data-testid="rec-actual"/>
            </div>
          </div>
          {actual && (
            <p className={`text-xs ${variance > 0 ? "text-rose-700" : variance < 0 ? "text-emerald-700" : "text-slate-500"}`}>
              Variance: {variance > 0 ? "+" : ""}{fmt(variance)} {variance > 0 ? "(overpaid vs estimate)" : variance < 0 ? "(underpaid vs estimate)" : ""}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Filed date</Label>
              <Input type="date" value={filedDate} onChange={e=>setFiledDate(e.target.value)} data-testid="rec-filed-date"/>
            </div>
            <div>
              <Label className="text-xs">Paid date <span className="text-rose-600">*</span></Label>
              <Input type="date" value={paidDate} onChange={e=>setPaidDate(e.target.value)} data-testid="rec-paid-date"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Filing reference</Label>
            <Input value={filingRef} onChange={e=>setFilingRef(e.target.value)} data-testid="rec-filing-ref"/>
          </div>
          <div>
            <Label className="text-xs">Bank account code</Label>
            <Input value={bank} onChange={e=>setBank(e.target.value)} data-testid="rec-bank"/>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={notes} onChange={e=>setNotes(e.target.value)} data-testid="rec-notes"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={submit} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="rec-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <Plus className="h-4 w-4 mr-1.5"/>}Save record + post JE
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
